<script setup lang="ts">
const { t, locale, locales } = useI18n();
const switchLocalePath = useSwitchLocalePath();

const { data } = await useAsyncData("contrats-footer", () => queryCollection("contracts").where("showInFooter", "=", true).select("slug", "title").all());
const { data: templates } = await useAsyncData("templates-footer", () => queryCollection("templates").where("showInFooter", "=", true).select("slug", "title").all());
const { data: allAlternatives } = await useAsyncData("alternatives-footer", () => queryCollection("alternatives").select("stem", "competitor").all());

const alternatives = computed(() => {
  if (!allAlternatives.value) return [];
  const prefix = locale.value === "fr" ? "alternatives/" : `${locale.value}/alternatives/`;
  return allAlternatives.value.filter((a) => a.stem?.startsWith(prefix));
});

const navigation = {
  social: [
    {
      name: "YouTube",
      to: "https://www.youtube.com/@contrat-ia",
      icon: "simple-icons:youtube",
    },
    {
      name: "Linkedin",
      to: "https://www.linkedin.com/company/contrat-ia",
      icon: "simple-icons:linkedin",
    },
    {
      name: "Bluesky",
      to: "https://bsky.app/profile/contrat-ia.com",
      icon: "simple-icons:bluesky",
    },
    {
      name: "GitHub",
      to: "https://github.com/contrat-ia",
      icon: "simple-icons:github",
    },
  ],
};

const entrepriseNav = computed(() => [
  // { name: t('footer.about'), to: "#" },
  { name: t('footer.blog'), to: "/blog" },
  { name: t('footer.termsOfService'), to: "/legal/conditions-d-utilisation" },
  { name: t('footer.privacyPolicy'), to: "/legal/confidentialite" },
  { name: t('footer.legalNotice'), to: "/legal/mentions-legales" },
  { name: t('common.contactUs'), to: "/contact" },
  // { name: t('footer.press'), to: "#" },
]);
</script>

<template>
  <footer class="mt-24 sm:mt-32 xl:mt-48">
    <div class="mx-auto max-w-7xl px-6 pb-8 pt-16 sm:pt-24 lg:px-8 lg:pt-32">
      <div class="xl:grid xl:grid-cols-3 xl:gap-8">
        <div class="space-y-5">
          <Logo to="/" />
          <p class="text-balance text-sm/6 text-gray-600">
            {{ $t('footer.tagline') }}
          </p>
          <div class="flex gap-x-6">
            <NuxtLink
              v-for="item in navigation.social"
              :key="item.name"
              no-prefetch
              target="_blank"
              rel="me"
              :to="item.to"
              class="text-gray-600 hover:text-gray-800"
            >
              <span class="sr-only">
                {{ item.name }}</span>
              <Icon
                :name="item.icon"
                class="size-5"
              />
            </NuxtLink>
          </div>
        </div>
        <div
          v-if="data"
          class="mt-16 grid gap-8 xl:col-span-2 xl:mt-0"
        >
          <div class="grid grid-cols-2 md:grid-cols-4 md:gap-8">
            <div>
              <h3 class="text-sm/6 font-semibold text-gray-900">
                {{ $t('footer.contractTypes') }}
              </h3>
              <ul
                role="list"
                class="mt-6 space-y-4"
              >
                <li
                  v-for="item in data"
                  :key="item.slug"
                >
                  <NuxtLinkLocale
                    no-prefetch
                    :to="`/contrat-en-ligne/${item.slug}`"
                    class="text-sm/6 text-gray-600 hover:text-gray-900"
                  >
                    {{ item.title }}
                  </NuxtLinkLocale>
                </li>
                <li>
                  <NuxtLinkLocale
                    no-prefetch
                    to="/contrat-en-ligne"
                    class="text-sm/6 text-gray-600 hover:text-gray-900"
                  >
                    {{ $t('footer.allContracts') }}
                  </NuxtLinkLocale>
                </li>
                <!-- <li>
                  <NuxtLink
                    no-prefetch
                    to="/modele"
                    class="text-sm/6 text-gray-600 hover:text-gray-900"
                  >
                    Modèles de contrats
                  </NuxtLink>
                </li> -->
              </ul>
            </div>
            <div>
              <h3 class="text-sm/6 font-semibold text-gray-900">
                {{ $t('footer.contractTemplates') }}
              </h3>
              <ul
                role="list"
                class="mt-6 space-y-4"
              >
                <li
                  v-for="item in templates"
                  :key="item.slug"
                >
                  <NuxtLinkLocale
                    no-prefetch
                    :to="`/modele/${item.slug}`"
                    class="text-sm/6 text-gray-600 hover:text-gray-900"
                  >
                    {{ item.title }}
                  </NuxtLinkLocale>
                </li>
                <li>
                  <NuxtLinkLocale
                    no-prefetch
                    to="/modele"
                    class="text-sm/6 text-gray-600 hover:text-gray-900"
                  >
                    {{ $t('footer.allTemplates') }}
                  </NuxtLinkLocale>
                </li>
              </ul>
            </div>
            <div class="mt-10 md:mt-0">
              <h3 class="text-sm/6 font-semibold text-gray-900">
                {{ $t('footer.comparisons') }}
              </h3>
              <ul
                role="list"
                class="mt-6 space-y-4"
              >
                <li
                  v-for="item in alternatives"
                  :key="item.stem"
                >
                  <NuxtLinkLocale
                    no-prefetch
                    :to="`/alternative/${item.stem?.replace(/^(.*\/)?alternatives\//, '')}`"
                    class="text-sm/6 text-gray-600 hover:text-gray-900"
                  >
                    {{ item.competitor }}
                  </NuxtLinkLocale>
                </li>
              </ul>
            </div>
            <div class="mt-10 md:mt-0">
              <h3 class="text-sm/6 font-semibold text-gray-900">
                {{ $t('footer.company') }}
              </h3>
              <ul
                role="list"
                class="mt-6 space-y-4"
              >
                <li
                  v-for="item in entrepriseNav"
                  :key="item.name"
                >
                  <NuxtLinkLocale
                    no-prefetch
                    :to="item.to"
                    class="text-sm/6 text-gray-600 hover:text-gray-900"
                  >
                    {{ item.name }}
                  </NuxtLinkLocale>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-16 border-t border-gray-900/10 pt-8 sm:mt-20 lg:mt-24 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p class="text-sm/6 text-gray-600">
          {{ $t('footer.copyright', { year: new Date().getFullYear() }) }}
          <br class="sm:hidden">
          <i18n-t
            keypath="footer.madeBy"
            tag="span"
          >
            <template #author>
              <NuxtLink
                to="https://mathix.dev"
                target="_blank"
                class="text-gray-600 hover:text-gray-900 hover:underline"
              >Arnaud Gissinger</NuxtLink>
            </template>
          </i18n-t>
        </p>
        <nav :aria-label="$t('footer.language')">
          <ul class="flex flex-wrap items-center gap-x-1 gap-y-1">
            <li
              v-for="(loc, i) in locales"
              :key="loc.code"
              class="flex items-center"
            >
              <NuxtLink
                :to="switchLocalePath(loc.code)"
                :lang="loc.code"
                :title="loc.name"
                class="text-xs text-gray-500 hover:text-gray-900 uppercase px-1 py-0.5 rounded transition-colors"
                :class="locale === loc.code ? 'font-semibold text-gray-900 bg-gray-100' : ''"
                :aria-current="locale === loc.code ? 'page' : undefined"
              >
                {{ loc.code }}
              </NuxtLink>
              <span
                v-if="i < locales.length - 1"
                class="text-gray-300"
                aria-hidden="true"
              >·</span>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </footer>
</template>
